import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { isPdrOrder } from '../../helpers/mlkActions';
import {
  getCorrectStatus,
  getExpectedServiceDateAndTimeSlot,
} from '../../helpers/order';
import {
  BlockedReason,
  CalculatedOrderStatus,
  Order,
  OrderState,
} from '../../models';
import { useOrder } from '../useOrder';

export const useIsEditDoorbellPositionEnabled = () => {
  const order = useOrder();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const isEditDoorbellPositionAvailable = useMemo(() => {
    if (order && cmsConfigs) {
      const calculatedOrderStatus = getCorrectStatus(order);
      return getIsEditDoorbellPositionAvailable(
        order,
        calculatedOrderStatus,
        cmsConfigs.featureFlags.isEditDoorbellPositionEnabled
      );
    }
    return null;
  }, [cmsConfigs, order]);

  return isEditDoorbellPositionAvailable;
};

const getIsEditDoorbellPositionAvailable = (
  order: Order,
  calculatedOrderStatus: CalculatedOrderStatus | undefined,
  isServiceEnabled: boolean
) => {
  const isLastAttempt = order.blockedReasons?.includes(BlockedReason.B01);
  const isPdr = isPdrOrder(order);
  const isSuspended = calculatedOrderStatus === CalculatedOrderStatus.Suspended;
  const isValidOrderState =
    order.status === OrderState.InTransit ||
    order.status === OrderState.ToReschedule;

  const isValidCalculatedStatus =
    calculatedOrderStatus !== undefined &&
    (calculatedOrderStatus === CalculatedOrderStatus.TooSoon ||
      calculatedOrderStatus === CalculatedOrderStatus.Masked ||
      calculatedOrderStatus === CalculatedOrderStatus.Missed ||
      calculatedOrderStatus === CalculatedOrderStatus.ProtectionMode ||
      calculatedOrderStatus === CalculatedOrderStatus.StandBy ||
      calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.ScheduledReception);

  const expectedDateAndTimeSlot = getExpectedServiceDateAndTimeSlot(order);
  let isSameDayPdr = false;
  if (expectedDateAndTimeSlot) {
    const today = dayjs();
    const hoursDifference = expectedDateAndTimeSlot.to
      .hour(0)
      .minute(0)
      .second(0)
      .diff(today, 'hour');
    isSameDayPdr = hoursDifference <= 0 && !!isPdr;
  }

  return (
    !isLastAttempt &&
    !isSameDayPdr &&
    !isSuspended &&
    (isValidOrderState || isValidCalculatedStatus) &&
    isServiceEnabled
  );
};
